import type {TFetchFoundUrlsArgs} from "store/extended-search/extendedSearchAsyncActions";

/**
 * Deciding whether a response that has just arrived is still the answer to the question being
 * asked.
 *
 * Split out of extendedSearchSlice.ts to keep it under the 300-line limit in CLAUDE.md, but it
 * reads better here anyway: these are the only pure functions in that file, and the reasoning
 * they encode took three passes and two Codex reviews to get right. Kept together so the two
 * halves cannot drift apart.
 */

/**
 * Records a dispatched request, newest last.
 *
 * Deliberately uncapped, and that is the third correction this file has taken. A fixed cap of 32
 * looked like ordinary hygiene against unbounded state and instead re-created the starvation it
 * was sitting next to: once latency passes 32 poll intervals — four minutes and a bit, and the
 * shared Axios client sets no timeout — every id is evicted before its answer arrives, so every
 * answer is discarded and the panel is stale or loading again. Any cap N does this at N
 * intervals. A Codex review on #2054 caught it.
 *
 * The bound is structural instead. Applying a response drops it and everything before it, so the
 * queue only holds requests dispatched since the last one that landed: one or two in normal use.
 * It grows solely while nothing lands at all — one entry per poll — and `useRunPolling` gives up
 * after 90 minutes, which caps that at a few hundred short strings. Closing the run, switching
 * run, changing the query and logging out each empty it outright.
 */
export const requestDispatched = (queue: string[], requestId: string): string[] => [
	...queue,
	requestId,
];

/**
 * The queue that should remain if this response is applied, or `null` if it must be discarded.
 *
 * A response is discarded only when its request is no longer in the queue, which happens once a
 * request dispatched *after* it has already been applied. A newer request merely being in flight
 * is not a reason to drop this one — that distinction is the whole point, and conflating the two
 * starves out every response whenever a request outlives the poll interval.
 */
export const remainingAfterApplying = (queue: string[], requestId: string): string[] | null => {
	const index = queue.indexOf(requestId);

	return index === -1 ? null : queue.slice(index + 1);
};

/**
 * Whether this is the most recently dispatched request, which is the bar a *failure* has to
 * clear rather than the one a success has to.
 *
 * The two are deliberately different. A successful older answer is worth showing while nothing
 * newer has landed — some data beats none. A failed older request is not worth reporting at all,
 * because a newer request is already on its way to a better answer, and an error banner naming a
 * run the operator has navigated away from outlives the request that caused it: nothing clears
 * `error` on a later success. So a failure speaks only for the newest request, and the newest
 * request always does speak, which is what keeps this from starving too.
 */
export const isNewestRequest = (queue: string[], requestId: string): boolean =>
	queue.length > 0 && queue[queue.length - 1] === requestId;

/**
 * Drops one settled request, leaving every other still eligible.
 *
 * For a failure, which must not take the requests around it down with it. Clearing the queue
 * instead — as the first version of this did — meant that when the newer of two overlapping
 * requests failed first, the older one's success was then ineligible; and since the pending
 * action has already emptied the rows, the panel stays blank behind an error with nothing left
 * to retry it. A Codex review on #2054 caught that. Reporting the failure and still accepting
 * the answer that does arrive is strictly better: the error is honest, and the operator gets
 * data rather than an empty table.
 */
export const withoutRequest = (queue: string[], requestId: string): string[] =>
	queue.filter((queued) => queued !== requestId);

/**
 * Identifies one discovered-URL request by everything that decides its answer.
 *
 * Not a substitute for the request id — two requests for this same key still race — but the
 * question a queued answer was asked, which is the other half of deciding whether to apply it.
 */
export const foundUrlsKeyOf = ({extendedSearchRunId, page, status}: TFetchFoundUrlsArgs): string =>
	`${extendedSearchRunId}:${page}:${status ?? ""}`;

/**
 * Records a dispatched request against the question it asks.
 *
 * Changing the question empties the queue, which is what discards answers to the previous one.
 * Ordering alone is not enough: a queued request for the filter the operator has just moved off
 * would otherwise be applied on arrival and render the old rows under the new controls — the
 * exact confusion the original guard existed to prevent. Caught by a Codex review on #2054, and
 * live for the run detail too, where an answer for a run just closed could land under the run
 * now open.
 */
export const requestDispatchedFor = (
	queue: string[],
	requestId: string,
	previousIdentity: string | number | null,
	identity: string | number,
): string[] => (identity === previousIdentity ? requestDispatched(queue, requestId) : [requestId]);
